import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { FieldDefinition, ItemTypeListItem, TelegramChatBinding, View } from '@lens/shared';
import { getItemType, listItemTypes } from '../item-types/api';
import { listViews } from '../views/api';
import {
  createTelegramConnectCode,
  createTelegramServiceFields,
  deleteTelegramBinding,
  listTelegramBindings,
  updateTelegramBinding,
} from './api';
import { TelegramSpaceNotifyCard } from './TelegramSpaceNotifyCard';

const { Text, Paragraph } = Typography;

interface TelegramTabProps {
  spaceId: string;
  myRole: 'admin' | 'member' | 'viewer';
}

/** Источники значений из сообщения и поля, куда их можно положить. */
const MAPPING_ROWS = [
  {
    key: 'text' as const,
    label: 'Текст сообщения',
    hint: 'Обычно «Описание»',
    types: ['text', 'textarea', 'markdown'],
    serviceField: 'text',
  },
  {
    key: 'title' as const,
    label: 'Первая строка (заголовок)',
    hint: 'Длинную обрежем до 80 символов',
    types: ['text', 'textarea', 'markdown'],
    serviceField: 'title',
  },
  {
    key: 'author' as const,
    label: 'Автор сообщения',
    hint: 'Заполнится, когда человек свяжет Telegram с учётной записью',
    types: ['user'],
    serviceField: 'author',
  },
  {
    key: 'date' as const,
    label: 'Дата сообщения',
    hint: '',
    types: ['date'],
    serviceField: 'date',
  },
];

export function TelegramTab({ spaceId, myRole }: TelegramTabProps): JSX.Element {
  const isAdmin = myRole === 'admin';
  const [loading, setLoading] = useState(true);
  const [bindings, setBindings] = useState<TelegramChatBinding[]>([]);
  const [itemTypes, setItemTypes] = useState<ItemTypeListItem[]>([]);
  const [views, setViews] = useState<View[]>([]);
  const [fieldsByType, setFieldsByType] = useState<Record<string, FieldDefinition[]>>({});
  const [connectCode, setConnectCode] = useState<{ code: string; expiresAt: string; botUsername: string | null } | null>(
    null,
  );
  const [saving, setSaving] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t, v] = await Promise.all([
        listTelegramBindings(spaceId),
        listItemTypes(spaceId),
        listViews(spaceId),
      ]);
      setBindings(b.bindings);
      setItemTypes(t);
      setViews(v.views);
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось загрузить настройки Telegram');
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Поля нужного типа записи подгружаем по требованию — их список рисуется
  // в выпадающих списках сопоставления.
  const ensureFields = useCallback(
    async (itemTypeId: string | null) => {
      if (!itemTypeId || fieldsByType[itemTypeId]) return;
      try {
        const type = await getItemType(spaceId, itemTypeId);
        setFieldsByType((prev) => ({ ...prev, [itemTypeId]: type.fields ?? [] }));
      } catch {
        // Молча: список полей просто останется пустым, выбирать будет не из чего.
      }
    },
    [fieldsByType, spaceId],
  );

  const effectiveTypeId = useCallback(
    (binding: TelegramChatBinding): string | null => {
      if (binding.itemTypeId) return binding.itemTypeId;
      if (!binding.viewId) return null;
      const view = views.find((v) => v.id === binding.viewId);
      return view?.itemTypeIds?.[0] ?? null;
    },
    [views],
  );

  useEffect(() => {
    bindings.forEach((b) => void ensureFields(effectiveTypeId(b)));
  }, [bindings, effectiveTypeId, ensureFields]);

  const handleConnect = async () => {
    try {
      const code = await createTelegramConnectCode(spaceId);
      setConnectCode(code);
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось получить код');
    }
  };

  const patch = async (binding: TelegramChatBinding, body: Parameters<typeof updateTelegramBinding>[2]) => {
    setSaving(binding.id);
    try {
      const updated = await updateTelegramBinding(spaceId, binding.id, body);
      setBindings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
      await ensureFields(effectiveTypeId(updated));
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось сохранить настройки чата');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (binding: TelegramChatBinding) => {
    try {
      await deleteTelegramBinding(spaceId, binding.id);
      setBindings((prev) => prev.filter((b) => b.id !== binding.id));
      void message.success('Чат отключён');
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось отключить чат');
    }
  };

  if (loading) return <Spin />;

  return (
    <div data-testid="telegram-tab">
      <TelegramSpaceNotifyCard spaceId={spaceId} isAdmin={isAdmin} />

      <Paragraph type="secondary">
        Записи создаются прямо из рабочего чата: участник пишет команду с текстом
        (<Text code>/task Не работает выгрузка</Text>) или отвечает командой на чужое сообщение — тогда
        в запись попадёт его текст. Бот ставит на сообщение 👍 — и больше ничего в чат не пишет.
        Подключение начинается здесь — в чате останется ввести код.
      </Paragraph>

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleConnect} disabled={!isAdmin}>
          Подключить чат
        </Button>
      </Space>

      {bindings.length === 0 ? (
        <Empty description="Ни один чат не подключён" />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {bindings.map((binding) => (
            <BindingCard
              key={binding.id}
              binding={binding}
              itemTypes={itemTypes}
              views={views}
              fields={fieldsByType[effectiveTypeId(binding) ?? ''] ?? []}
              typeId={effectiveTypeId(binding)}
              saving={saving === binding.id}
              isAdmin={isAdmin}
              spaceId={spaceId}
              onPatch={(body) => patch(binding, body)}
              onDelete={() => handleDelete(binding)}
              onFieldsCreated={(typeId, created) => {
                setFieldsByType((prev) => {
                  const next = { ...prev };
                  delete next[typeId];
                  return next;
                });
                void ensureFields(typeId);
                void message.success(`Созданы поля: ${created.join(', ')}`);
              }}
            />
          ))}
        </Space>
      )}

      <Modal
        open={Boolean(connectCode)}
        onCancel={() => {
          setConnectCode(null);
          void reload();
        }}
        title="Подключение чата"
        footer={
          <Button
            type="primary"
            onClick={() => {
              setConnectCode(null);
              void reload();
            }}
          >
            Готово
          </Button>
        }
      >
        <Paragraph>Добавьте бота в рабочий чат и отправьте там сообщение:</Paragraph>
        <Paragraph>
          <Text code copyable style={{ fontSize: 18 }}>
            /привязать {connectCode?.code}
          </Text>
        </Paragraph>
        <Paragraph type="secondary">
          Код действует 15 минут и гасится после подключения. Бот
          {connectCode?.botUsername ? ` — @${connectCode.botUsername}` : ''}. Так Lens узнаёт номер
          приватного чата: по ссылке-приглашению его получить нельзя.
        </Paragraph>
      </Modal>
    </div>
  );
}

interface BindingCardProps {
  binding: TelegramChatBinding;
  itemTypes: ItemTypeListItem[];
  views: View[];
  fields: FieldDefinition[];
  typeId: string | null;
  saving: boolean;
  isAdmin: boolean;
  spaceId: string;
  onPatch: (body: Parameters<typeof updateTelegramBinding>[2]) => void;
  onDelete: () => void;
  onFieldsCreated: (typeId: string, created: string[]) => void;
}

function BindingCard({
  binding,
  itemTypes,
  views,
  fields,
  typeId,
  saving,
  isAdmin,
  spaceId,
  onPatch,
  onDelete,
  onFieldsCreated,
}: BindingCardProps): JSX.Element {
  const [creatingFields, setCreatingFields] = useState(false);
  const [mode, setMode] = useState<'type' | 'view'>(binding.viewId ? 'view' : 'type');

  const optionsFor = (types: string[]) =>
    fields
      .filter((f) => types.includes(f.fieldType))
      .map((f) => ({ value: f.id, label: f.name }));

  // Строки, для которых в типе записи нет ни одного подходящего поля —
  // именно их предлагает завести кнопка «Создать недостающие поля».
  const missing = MAPPING_ROWS.filter((row) => optionsFor(row.types).length === 0).map(
    (row) => row.serviceField,
  );

  const handleCreateFields = async () => {
    if (!typeId) return;
    setCreatingFields(true);
    try {
      const result = await createTelegramServiceFields(spaceId, typeId, missing);
      onFieldsCreated(typeId, result.created);
      if (Object.keys(result.mapping).length > 0) {
        onPatch({ fieldMapping: result.mapping });
      }
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось создать поля');
    } finally {
      setCreatingFields(false);
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>{binding.chatTitle ?? `Чат ${binding.chatId}`}</span>
          {!binding.enabled && <Tag>выключен</Tag>}
        </Space>
      }
      extra={
        <Space>
          <Switch
            checked={binding.enabled}
            onChange={(enabled) => onPatch({ enabled })}
            disabled={!isAdmin || saving}
            checkedChildren="вкл"
            unCheckedChildren="выкл"
          />
          <Popconfirm
            title="Отключить чат?"
            description="Записи останутся, но новые из этого чата создаваться не будут."
            onConfirm={onDelete}
            okText="Отключить"
            cancelText="Отмена"
            disabled={!isAdmin}
          >
            <Button icon={<DeleteOutlined />} danger type="text" disabled={!isAdmin} />
          </Popconfirm>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space wrap>
          <Text type="secondary">Куда создавать:</Text>
          <Segmented
            value={mode}
            options={[
              { value: 'type', label: 'Тип записи' },
              { value: 'view', label: 'Представление' },
            ]}
            disabled={!isAdmin || saving}
            onChange={(value) => {
              const next = value as 'type' | 'view';
              setMode(next);
              // Переключение режима сбрасывает прежнюю цель: держать обе
              // сразу нельзя, сервис такое сохранение отклонит.
              if (next === 'type' && binding.viewId) onPatch({ viewId: null, itemTypeId: null });
              if (next === 'view' && binding.itemTypeId) onPatch({ itemTypeId: null, viewId: null });
            }}
          />
          {mode === 'view' ? (
            <Select
              style={{ minWidth: 260 }}
              placeholder="Выберите представление"
              value={binding.viewId ?? undefined}
              options={views.map((v) => ({ value: v.id, label: v.name }))}
              onChange={(viewId) => onPatch({ viewId, itemTypeId: null })}
              disabled={!isAdmin || saving}
              showSearch
              optionFilterProp="label"
            />
          ) : (
            <Select
              style={{ minWidth: 260 }}
              placeholder="Выберите тип записи"
              value={binding.itemTypeId ?? undefined}
              options={itemTypes.map((t) => ({ value: t.id, label: t.name }))}
              onChange={(itemTypeId) => onPatch({ itemTypeId, viewId: null })}
              disabled={!isAdmin || saving}
              showSearch
              optionFilterProp="label"
            />
          )}
        </Space>

        <Text type="secondary" style={{ fontSize: 12 }}>
          {mode === 'view'
            ? 'Тип записи берётся из представления, а его фильтры вида «поле = значение» проставляются в новую запись — например, «Квартал = Q3 2026». Так записи из чата сразу попадают в нужный срез.'
            : 'Записи будут создаваться в этом типе. Если нужно, чтобы им сразу проставлялись какие-то поля, выберите вместо типа представление с фильтрами «поле = значение».'}
        </Text>

        {typeId ? (
          <>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={MAPPING_ROWS}
              columns={[
                {
                  title: 'Что приходит из Telegram',
                  dataIndex: 'label',
                  render: (label: string, row) => (
                    <Space direction="vertical" size={0}>
                      <span>{label}</span>
                      {row.hint && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {row.hint}
                        </Text>
                      )}
                    </Space>
                  ),
                },
                {
                  title: 'Куда положить',
                  width: 320,
                  render: (_, row) => {
                    const options = optionsFor(row.types);
                    return (
                      <Select
                        style={{ width: '100%' }}
                        allowClear
                        placeholder={options.length === 0 ? 'Нет подходящего поля' : 'Не переносить'}
                        value={binding.fieldMapping[row.key] ?? undefined}
                        options={options}
                        disabled={!isAdmin || saving || options.length === 0}
                        onChange={(value) => onPatch({ fieldMapping: { [row.key]: value ?? null } })}
                        showSearch
                        optionFilterProp="label"
                      />
                    );
                  },
                },
              ]}
            />

            {missing.length > 0 && isAdmin && (
              <Alert
                type="warning"
                showIcon
                message="В типе записи не хватает полей для части источников"
                action={
                  <Button size="small" loading={creatingFields} onClick={handleCreateFields}>
                    Создать недостающие поля
                  </Button>
                }
              />
            )}
          </>
        ) : (
          <Alert type="warning" showIcon message="Выберите тип записи или представление — без этого записи не создаются." />
        )}

        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Команда в чате
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Слово, с которого начинается сообщение, чтобы из него завели запись:
            <Text code>/task Не работает выгрузка</Text>. Бот помечает такое сообщение 👍 и больше
            ничего в чат не пишет. Отдельная команда <Text code>/account</Text> выдаёт в чат кнопку
            «Связать аккаунт» — отправьте её один раз и закрепите, тогда участники будут значиться
            авторами своих записей.
          </Text>
          <Input
            style={{ width: 200 }}
            placeholder="/task"
            defaultValue={binding.triggers.command ?? ''}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== (binding.triggers.command ?? '')) {
                onPatch({ triggers: { command: value || null } });
              }
            }}
            disabled={!isAdmin || saving}
          />
        </div>

        <Space wrap>
          <Text type="secondary">Кто может создавать:</Text>
          <Segmented
            value={binding.whoCanCreate}
            options={[
              { value: 'all', label: 'Все участники чата' },
              { value: 'linked', label: 'Только связавшие аккаунт' },
            ]}
            onChange={(value) => onPatch({ whoCanCreate: value as 'all' | 'linked' })}
            disabled={!isAdmin || saving}
          />
        </Space>

        <div>
          <Space wrap>
            <Switch
              checked={binding.announceNew}
              onChange={(announceNew) => onPatch({ announceNew })}
              disabled={!isAdmin || saving}
            />
            <Text>Сообщать в чат о новых записях</Text>
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Бот пишет строку со ссылкой, когда запись заводят в интерфейсе Lens — по всему
            пространству, а не только по типу записи из настройки выше. Загрузка из файла,
            синхронизация с Jira, ИИ-помощник и записи из самого чата в чат не попадают.
          </Text>
        </div>

      </Space>
    </Card>
  );
}
